import { weekFileName } from '../lib/weekFileName';
import { DocLangToggle } from './ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { Button } from './ui/button';
import PermButton from './shared/PermButton';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { FileText, Users, Building2, Calendar, FileSpreadsheet, Clock, UserCog } from 'lucide-react';
import axios from 'axios';
import TaskReportsTab from './TaskReportsTab';
import CustomEmployeeReport from './CustomEmployeeReport';
import useSubTabShortcut from '../hooks/useSubTabShortcut';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrintReports = ({ user }) => {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState({});
  const [subTab, setSubTab] = useState('custom-employee');
  useSubTabShortcut({
    tab: 'reports', ctxKey: 'reportsSubTab', value: subTab, setValue: setSubTab,
    labels: { 'custom-employee': 'auto.misc.customEmployeeReport', general: 'auto.misc.generalReports', tasks: 'auto.misc.tasksTimeTracking' },
  });

  const downloadExcelReport = async (reportType, reportName) => {
    setDownloading(prev => ({ ...prev, [reportType]: true }));
    
    try {
      const response = await axios.get(`${API}/reports/${reportType}/excel`, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', weekFileName(`${reportName.toLowerCase().replace(/\s+/g, '_').replace(/_report$/, '')}_report.xlsx`));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('docLang.downloaded', { name: reportName }));
    } catch (error) {
      console.error(`Error downloading ${reportName} report:`, error);
      toast.error(t('docLang.downloadFailed', { name: reportName }));
    } finally {
      setDownloading(prev => ({ ...prev, [reportType]: false }));
    }
  };

  const reports = [
    {
      id: 'employees',
      name: 'Employee Report',
      description: t('auto.lbl.completeListOfAllEmployeesWith'),
      icon: Users,
      color: 'bg-blue-500',
      disabled: false
    },
    {
      id: 'customers',
      name: 'Customer Report', 
      description: t('auto.lbl.completeListOfAllCustomersWith'),
      icon: Building2,
      color: 'bg-green-500',
      disabled: false
    },
    {
      id: 'projects',
      name: 'Project Report',
      description: t('auto.lbl.completeListOfAllProjectsWith'),
      icon: FileText,
      color: 'bg-purple-500',
      disabled: false
    }
  ];

  if (user?.role === 'employee') {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">{t('auto.misc.accessRestricted')}</p>
            <p className="text-sm text-gray-400">{t('auto.misc.onlyAdministratorsAndManagersCanGenerate')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <FileText className="h-8 w-8 text-[#AA0000]" />
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{t('auto.misc.reports')}</h2>
          <p className="text-slate-600">{t('auto.misc.generateProfessionalReportsWithCompanyBranding')}</p>
        </div>
      </div>

      <Tabs value={subTab} onValueChange={setSubTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="custom-employee" className="flex items-center gap-1" data-testid="custom-employee-report-subtab">
            <UserCog className="h-4 w-4" />{t('auto.misc.customEmployeeReport')}
          </TabsTrigger>
          <TabsTrigger value="general" className="flex items-center gap-1" data-testid="general-reports-subtab">
            <FileSpreadsheet className="h-4 w-4" />{t('auto.misc.generalReports')}
          </TabsTrigger>
          <TabsTrigger value="tasks" className="flex items-center gap-1" data-testid="task-reports-subtab">
            <Clock className="h-4 w-4" />{t('auto.misc.tasksTimeTracking')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="custom-employee" className="space-y-4">
          <CustomEmployeeReport />
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <TaskReportsTab />
        </TabsContent>

        <TabsContent value="general" className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reports.map((report) => {
          const Icon = report.icon;
          const isDownloading = downloading[report.id];
          
          return (
            <Card key={report.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className={`p-3 rounded-lg ${report.color} text-white`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{report.name}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">{report.description}</p>
                
                <div className="space-y-2">
                  <div className="text-xs text-gray-500">
                    ✓ Professional formatting<br/>
                    ✓ Complete data export<br/>
                    ✓ Multiple worksheets<br/>
                    ✓ Date/time generated
                  </div>
                </div>
                
                <div className="mt-3 flex justify-end"><DocLangToggle testId={`doc-lang-report-${report.id}`} /></div>
                <PermButton
                  perm="reports.export"
                  onClick={() => downloadExcelReport(report.id, report.name)}
                  disabled={isDownloading || report.disabled}
                  className="w-full mt-4"
                  data-testid={`download-excel-${report.id}`}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  {isDownloading ? t('auto.misc.generating') : t('auto.misc.downloadExcel')}
                </PermButton>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>{t('auto.misc.timesheetReports')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-2">{t('auto.misc.weeklyTimesheetReports')}</p>
            <p className="text-sm text-gray-400 mb-4">
              {t('auto.misc.goToTheWeeklyTimesheetsTab')}
            </p>
            <div className="text-xs text-gray-500">
              ✓ Weekly hour tracking matrix<br/>
              ✓ Employee and project breakdowns<br/>
              ✓ Total hours summary<br/>
              ✓ Professional formatting with company branding
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('auto.misc.reportFeatures')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">{t('auto.misc.professionalFormatting')}</h4>
              <ul className="space-y-1 text-gray-600">
                <li>{t('auto.misc.companyLogoAndHeader')}</li>
                <li>{t('auto.misc.professionalTableLayouts')}</li>
                <li>{t('auto.misc.consistentBranding')}</li>
                <li>{t('auto.misc.printReadyFormatting')}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">{t('auto.misc.dataIncluded')}</h4>
              <ul className="space-y-1 text-gray-600">
                <li>{t('auto.misc.completeRecordDetails')}</li>
                <li>{t('auto.misc.summaryStatistics')}</li>
                <li>{t('auto.misc.generationTimestamp')}</li>
                <li>{t('auto.misc.confidentialityFooter')}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PrintReports;